import { formatCurrency } from "@design-lab/shared/lib/currency";
import { cn } from "@design-lab/shared/lib/tailwindcss";
import type { InventoryError } from "@design-lab/shared/types";
import { Button } from "@design-lab/shared/ui/design-system/Button";
import { Tooltip, TooltipTrigger } from "@design-lab/shared/ui/design-system/Tooltip";
import type { ID } from "common/utils/models";
import { Minus, Plus, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { BasePrice } from "./calculateBasePrice";

type SizeItemProps = {
  variantId: ID;
  variantSize: string;
  quantity: number;
  isOutOfStock: boolean;
  inventoryCount: string;
  inventoryError?: InventoryError;
  basePrice: BasePrice | undefined;
  getVariantPrice: (variantId: string) => string | undefined;
  onIncrement: (variantId: ID) => void;
  onDecrement: (variantId: ID) => void;
  onInputChange: (variantId: ID, value: string) => void;
  isSample?: boolean;
};

export function SizeItem(props: SizeItemProps) {
  const {
    variantId,
    variantSize,
    quantity,
    isOutOfStock,
    inventoryCount,
    inventoryError,
    basePrice,
    getVariantPrice,
    onIncrement,
    onDecrement,
    onInputChange,
    isSample = false
  } = props;
  const price = getVariantPrice(variantId);
  // Check if current quantity exceeds available stock
  const availableStock = Math.max(0, parseInt(inventoryCount, 10) || 0);
  const isInventoryLoaded = inventoryCount !== "..." && !Number.isNaN(availableStock);
  const exceedsStock =
    isInventoryLoaded && quantity > 0 && quantity > availableStock && availableStock > 0;

  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const hasInventoryError = Boolean(inventoryError) || exceedsStock;

  // Auto-show tooltip when there's an error, auto-hide when resolved
  useEffect(() => {
    setIsTooltipOpen(hasInventoryError || exceedsStock);
  }, [hasInventoryError, exceedsStock]);

  const handleInputChange = (value: string) => {
    const numValue = parseInt(value, 10);

    if (value === "" || (!Number.isNaN(numValue) && numValue >= 0 && numValue <= 999)) {
      onInputChange(variantId, value);
    }
  };

  const handleIncrement = () => {
    if (quantity < 999) {
      onIncrement(variantId);
    }
  };

  const handleDecrement = () => {
    onDecrement(variantId);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent non-integer characters from being typed that are allowed by type number
    if (e.key === "+" || e.key === "-" || e.key === "." || e.key === "e" || e.key === "E") {
      e.preventDefault();
    }
  };

  const priceLabel = useMemo(() => {
    if (!basePrice || !price) {
      return price;
    }

    const priceNumber = Number(price);

    if (basePrice.value === priceNumber) {
      return undefined;
    }

    return priceNumber - basePrice.value;
  }, [basePrice, price]);

  // In sample mode, buttons are disabled (quantity is fixed at 1 for L size)
  const buttonsDisabled = isSample;

  if (isOutOfStock) {
    return (
      <div className="flex w-20 flex-col items-center">
        <div className="mb-1 text-center text-sm font-bold text-gray-800">{variantSize}</div>
        <div className="mb-1 flex h-8 w-20 items-center justify-center rounded border border-gray-200 bg-gray-50 px-3 py-2">
          <X size={16} className="text-gray-800" />
        </div>
        <div className="text-center text-xs">
          <span className="text-gray-400">Out of Stock</span>
        </div>
      </div>
    );
  }

  const getTextColor = () => {
    if (hasInventoryError) return "text-red-600";
    if (buttonsDisabled) return "text-gray-400";
    return "text-gray-800";
  };

  const styles = {
    border: hasInventoryError ? "border-red-500" : "border-gray-300",
    text: getTextColor(),
    label: hasInventoryError ? "text-red-600" : "text-gray-800"
  };

  return (
    <div
      className="relative flex w-20 flex-col items-center"
      data-testid={`size-item-${variantSize}`}
    >
      <div className={cn("mb-1 text-center text-sm font-bold", styles.label)}>{variantSize}</div>
      <div
        className={cn(
          "mb-1 flex h-8 w-20 items-center justify-center gap-2 rounded border px-3 py-2",
          styles.border
        )}
      >
        <button
          data-testid={`decrease-${variantSize}-button`}
          aria-label={`Decrease quantity for size ${variantSize}`}
          onClick={handleDecrement}
          disabled={quantity <= 0 || buttonsDisabled}
          className="text-gray-400 hover:text-gray-800 disabled:text-gray-300"
        >
          <Minus size={16} />
        </button>
        <input
          data-testid={`quantity-${variantSize}-input`}
          type="number"
          value={quantity === 0 ? "" : quantity}
          placeholder="0"
          aria-label={`Quantity for size ${variantSize}`}
          pattern="[0-9]*"
          onChange={e => !buttonsDisabled && handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          className={cn(
            "w-6 border-none bg-transparent text-center text-base font-bold",
            "placeholder-gray-400 [appearance:textfield] focus:outline-none lg:text-sm",
            "[&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none",
            "[&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none",
            styles.text
          )}
          inputMode="numeric"
          min={0}
          max={Math.min(parseInt(inventoryCount, 10), 999)}
          disabled={buttonsDisabled}
          readOnly={buttonsDisabled}
        />
        <button
          data-testid={`increase-${variantSize}-button`}
          aria-label={`Increase quantity for size ${variantSize}`}
          onClick={handleIncrement}
          disabled={buttonsDisabled}
          className={cn(buttonsDisabled ? "text-gray-300" : "text-gray-800 hover:text-gray-600")}
        >
          <Plus size={16} />
        </button>
      </div>

      {priceLabel && (
        <div className="text-center text-xs font-semibold text-green-700">
          {Number(priceLabel) > 0 && "+"}
          {formatCurrency(priceLabel)}
        </div>
      )}

      {/* Invisible button overlay for tooltip positioning */}
      {(hasInventoryError || exceedsStock) && (
        <TooltipTrigger isOpen={isTooltipOpen} delay={0}>
          <Button
            aria-label="Inventory error"
            className="absolute inset-0 cursor-default rounded-none border-none bg-transparent shadow-none"
            style={{ pointerEvents: "none" }}
          />
          <Tooltip
            placement="top"
            showArrow={false}
            offset={8}
            className="bg-background whitespace-nowrap border border-red-500 px-3 py-2 text-sm text-gray-700 shadow-xl"
          >
            Only {availableStock} in stock. Reduce quantity.
          </Tooltip>
        </TooltipTrigger>
      )}
    </div>
  );
}
